import React from 'react';

/**
 * Componente apresentacional puro (sem fetch, sem dependência de usuário
 * logado) pra exibir um mapa astral genérico: roda zodiacal + planetas +
 * casas + aspectos. Recebe `chartData` no mesmo formato retornado por
 * generate_birth_chart() / compute_composite_chart() / compute_davison_chart()
 * no backend.
 *
 * Diferente de FullAstralMap.tsx (que assume campos enriquecidos por IA por
 * item — planet.interpretation, house.interpretation, etc., só presentes no
 * mapa natal do próprio usuário), aqui mostramos só os dados calculados
 * (signo/casa/grau, cúspides, tipo+orbe de aspecto) — é o que Mapa Composto e
 * Mapa de Davison têm disponível.
 */

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-center gap-3 mb-4">
    <span className="text-amber-400 text-sm">✦</span>
    <h3 className="text-xs font-bold text-slate-200 uppercase tracking-[0.2em]">{children}</h3>
    <div className="flex-1 h-px bg-gradient-to-r from-white/10 to-transparent" />
  </div>
);

interface ChartDisplayProps {
  chartData: any;
  title?: string;
}

const ChartDisplay: React.FC<ChartDisplayProps> = ({ chartData, title }) => {
  if (!chartData) return null;
  const planets: any[] = Array.isArray(chartData.planets) ? chartData.planets : [];
  const houses: any[] = Array.isArray(chartData.houses) ? chartData.houses : [];
  const aspects: any[] = Array.isArray(chartData.aspects) ? chartData.aspects : [];

  // Texto explicativo curto por item: calculado no backend (sem IA) em
  // detailed_report, na mesma ordem dos arrays acima — casa a posição por
  // índice em vez de depender do item já ter um campo `interpretation`
  // (que só o mapa natal do próprio usuário ganha, via IA).
  const report = chartData.detailed_report || {};
  const planetsReport: { interpretation?: string }[] = Array.isArray(report.planets) ? report.planets : [];
  const housesReport: { interpretation?: string }[] = Array.isArray(report.houses) ? report.houses : [];
  const aspectsReport: { interpretation?: string }[] = Array.isArray(report.aspects) ? report.aspects : [];

  return (
    <div className="space-y-8">
      {title && <h2 className="text-lg font-semibold text-white">{title}</h2>}

      {chartData.chart_image && (
        <div>
          <SectionTitle>Roda Zodiacal</SectionTitle>
          <div className="flex justify-center">
            <img src={chartData.chart_image} alt={title || 'Mapa astral'} className="max-h-[420px] rounded-2xl border border-white/10" />
          </div>
        </div>
      )}

      {planets.length > 0 && (
        <div>
          <SectionTitle>Posições Planetárias</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {planets.map((p: any, i: number) => (
              <div key={i} className="bg-white/[0.08] border border-white/[0.14] rounded-xl p-4">
                <div className="font-semibold text-white text-sm">{p.planet}</div>
                <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                  <span className="text-xs text-amber-300/80">{p.sign}</span>
                  {typeof p.house !== 'undefined' && p.house !== null && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-white/[0.07] border border-white/10 rounded-full text-slate-400">Casa {p.house}</span>
                  )}
                  {typeof p.degree !== 'undefined' && (
                    <span className="text-[10px] text-slate-500">{Number(p.degree).toFixed(1)}°{p.retrograde ? ' ℞' : ''}</span>
                  )}
                </div>
                {planetsReport[i]?.interpretation && (
                  <p className="text-xs text-slate-400 leading-relaxed mt-2">{planetsReport[i].interpretation}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {houses.length > 0 && (
        <div>
          <SectionTitle>Casas</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {houses.map((h: any, i: number) => (
              <div key={i} className="bg-white/[0.08] border border-white/[0.14] rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 shrink-0 rounded-full bg-amber-400/10 border border-amber-400/20 flex items-center justify-center">
                    <span className="text-amber-400 text-xs font-bold">{h.house}</span>
                  </div>
                  <span className="text-xs text-slate-300">{h.cusp_sign} {Number(h.cusp_degree).toFixed(1)}°</span>
                </div>
                {housesReport[i]?.interpretation && (
                  <p className="text-xs text-slate-400 leading-relaxed mt-2">{housesReport[i].interpretation}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {aspects.length > 0 && (
        <div>
          <SectionTitle>Aspectos</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {aspects.map((a: any, i: number) => (
              <div key={i} className="bg-white/[0.08] border border-white/[0.14] rounded-xl p-4">
                <div className="text-sm font-medium text-white">{a.planet1} <span className="text-purple-400">{a.type}</span> {a.planet2}</div>
                <div className="text-[10px] text-slate-400 mt-1">Orbe {a.orb}°</div>
                {aspectsReport[i]?.interpretation && (
                  <p className="text-xs text-slate-400 leading-relaxed mt-2">{aspectsReport[i].interpretation}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartDisplay;
